import { useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row, Table } from "react-bootstrap";
import useAxiosPrivate from "../../../Pages/hooks/useAxiosPrivate";
import { baseURL } from "../../../API/API";
import { toast } from "react-toastify";
import Lodingsubmit from "../../Loding/Loding";
import { FaWallet, FaBook, FaCoins, FaGraduationCap } from "react-icons/fa";
import "./Content.css";

export default function MonthlySummary() {
  const axiosPrivate = useAxiosPrivate();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadSummary() {
      setLoading(true);
      try {
        const res = await axiosPrivate.get(`${baseURL}/learning/monthly-summary`, { params: { year, month } });
        setData(res.data?.data || null);
      } catch (err) {
        toast.error(err.response?.data?.message || "فشل تحميل ملخص الشهر");
      } finally {
        setLoading(false);
      }
    }
    loadSummary();
  }, [axiosPrivate, year, month]);

  const totals = data?.totals || { payments: 0, sales: 0, total: 0, events_count: 0, access_unlocks: 0 };
  const years = Array.from({ length: 5 }, (_, index) => now.getFullYear() - index);

  return (
    <div className="p-4 content" dir="rtl">
      {loading && <Lodingsubmit />}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">ملخص الشهر</h2>
          <p className="text-muted m-0">سجل شهري يوم بيوم للتحويلات والاشتراكات والأرباح.</p>
        </div>
        <div className="d-flex gap-2">
          <Form.Select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ minWidth: 140 }}>
            {Array.from({ length: 12 }, (_, index) => index + 1).map((item) => <option key={item} value={item}>شهر {item}</option>)}
          </Form.Select>
          <Form.Select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ minWidth: 120 }}>
            {years.map((item) => <option key={item} value={item}>{item}</option>)}
          </Form.Select>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <SummaryCard title="إجمالي الداخل" value={totals.total} />
        <SummaryCard title="التحويلات المقبولة" value={totals.payments} />
        <SummaryCard title="مبيعات الكورسات" value={totals.sales} />
        <SummaryCard title="عدد العمليات" value={totals.events_count} suffix="عملية" money={false} />
      </Row>

      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 8 }}>
        <Card.Body>
          <h5 className="fw-bold mb-3">تفصيل الأيام</h5>
          <div className="table-responsive">
            <Table hover className="align-middle text-center">
              <thead>
                <tr>
                  <th>اليوم</th>
                  <th>التحويلات</th>
                  <th>المبيعات</th>
                  <th>الإجمالي</th>
                  <th>عدد الطلاب/العمليات</th>
                </tr>
              </thead>
              <tbody>
                {(data?.daily || []).map((row) => (
                  <tr key={row.day}>
                    <td>{row.date}</td>
                    <td>{Number(row.payments).toFixed(0)} ج.م</td>
                    <td>{Number(row.sales).toFixed(0)} ج.م</td>
                    <td className="fw-bold">{Number(row.total).toFixed(0)} ج.م</td>
                    <td>{row.operations ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm" style={{ borderRadius: 8 }}>
        <Card.Body>
          <h5 className="fw-bold mb-3">سجل العمليات</h5>
          <div className="table-responsive">
            <Table hover className="align-middle text-center">
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>الطالب</th>
                  <th>النوع</th>
                  <th>المدرس</th>
                  <th>العنصر</th>
                  <th>المبلغ</th>
                  <th>التفاصيل</th>
                </tr>
              </thead>
              <tbody>
                {(data?.events || []).length === 0 ? (
                  <tr><td colSpan="7" className="text-muted py-4">لا توجد عمليات في هذا الشهر.</td></tr>
                ) : (data.events.map((event) => (
                  <tr key={event.id}>
                    <td>{event.date}</td>
                    <td>{event.student}</td>
                    <td>
                      {event.item_type === "شحن رصيد" ? (
                        <span className="badge bg-success-light text-success d-inline-flex align-items-center gap-1">
                          <FaWallet /> {event.item_type}
                        </span>
                      ) : (
                        <span className="badge bg-primary-light text-primary d-inline-flex align-items-center gap-1">
                          <FaBook /> {event.item_type}
                        </span>
                      )}
                    </td>
                    <td>{event.teacher_name}</td>
                    <td>
                      <span className="d-inline-flex align-items-center gap-1">
                        {event.item_name === "رصيد محفظة" ? <FaCoins className="text-warning" /> : <FaGraduationCap className="text-info" />}
                        {event.item_name}
                      </span>
                    </td>
                    <td className="fw-bold">{Number(event.amount).toFixed(0)} ج.م</td>
                    <td className="text-end" style={{ minWidth: 260 }}>{event.description}</td>
                  </tr>
                ))) }
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

function SummaryCard({ title, value, suffix = "ج.م", money = true }) {
  return (
    <Col md={3} sm={6}>
      <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
        <Card.Body className="text-end">
          <div className="text-muted small mb-2">{title}</div>
          <div className="fw-bold" style={{ fontSize: "1.6rem", color: "var(--brand-300)" }}>
            {money ? Number(value || 0).toFixed(0) : value || 0} <span className="fs-6 text-muted">{suffix}</span>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
}